import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Uuid, uuid } from '@softline/core';
import {
  ErrorMessageBarConfig,
  InfoMessageBarConfig,
  LoadingMessageBarConfig,
  MessageBarAction,
  MessageBarConfig, ProgressMessageBarConfig,
  SavingMessageBarConfig, SuccessMessageBarConfig, WarningMessageBarConfig
} from './data/message-bar.config';
import { SOFTLINE_CONFIG_MESSAGE_BAR_DURATION } from '@softline/ui-core';
import {translator} from '../i18n/functions/translator.function';

export interface MessageBarOptions {
  actions?: MessageBarAction[];
  duration?: number;
  onDismiss?(): void;
}

@Injectable({providedIn: 'root'})
export class MessageBarService {
  private t = translator()
  private _messageBars = signal<readonly Readonly<MessageBarConfig & {id: Uuid}>[]>([]);

  duration = inject(SOFTLINE_CONFIG_MESSAGE_BAR_DURATION);
  messageBars = computed(() => this._messageBars());

  constructor() {}

  open(config: MessageBarConfig): Uuid {
    const messageBars = [...this.messageBars()];

    if(typeof config.message === 'string' && config.message.startsWith('#'))
      config.message = this.t(config.message);
    if(config.title && typeof config.title === 'string' && config.title.startsWith('#'))
      config.title = this.t(config.title);

    const id =  uuid();
    messageBars.push({...config, id});
    this._messageBars.set(messageBars);
    if ((config.duration ?? 0) < Number.POSITIVE_INFINITY) {
      setTimeout(() => {
        const messageBars = [...this.messageBars()];
        const index = messageBars.findIndex(o => o.id === id);
        if (index > -1)
          this.close(id);
      }, config.duration ?? this.duration);
    }
    return id;
  }

  close(id: Uuid): Uuid {
    const messageBars = [...this.messageBars()];
    const index = messageBars.findIndex(o => o.id === id);
    if (index === -1)
      throw new Error(`[MessageBarService] close: No message bar found with id ${id}`);
    messageBars.splice(index, 1);
    this._messageBars.set(messageBars);
    return id;
  }

  closeAll(): void {
    this._messageBars.set([]);
  }

  success(message: string | Signal<string>, title?: string | Signal<string> | null, options?: MessageBarOptions): Uuid
  {
    const config: SuccessMessageBarConfig = {
      type: 'success',
      ...options,
      message,
      title: title ?? '#UI_CORE.MESSAGE_BAR.SUCCESS_TITLE',
      icon: 'fa-solid fa-circle-check',
      dismiss: true,
    };
    return this.open(config);
  }

  warning(message: string | Signal<string>, title?: string | Signal<string> | null, options?: MessageBarOptions): Uuid
  {
    const config: WarningMessageBarConfig = {
      type: 'warning',
      ...options,
      message,
      title: title ?? '#UI_CORE.MESSAGE_BAR.WARNING_TITLE',
      icon: 'fa-solid fa-circle-exclamation',
      dismiss: true,
    };
    return this.open(config);
  }

  error(message: string | Signal<string>, title?: string | Signal<string> | null, options?: MessageBarOptions): Uuid
  {
    const config: ErrorMessageBarConfig = {
      type: 'error',
      ...options,
      message,
      title: title ?? '#UI_CORE.MESSAGE_BAR.ERROR_TITLE',
      icon: 'fa-solid fa-circle-xmark',
      duration: options?.duration ?? Number.POSITIVE_INFINITY,
      dismiss: true,
    };
    return this.open(config);
  }

  info(message: string | Signal<string>, title?: string | Signal<string> | null, options?: MessageBarOptions): Uuid
  {
    const config: InfoMessageBarConfig = {
      type: 'info',
      ...options,
      message,
      title: title ?? '#UI_CORE.MESSAGE_BAR.INFO_TITLE',
      icon: 'fa-solid fa-circle-info',
      dismiss: true,
    };
    return this.open(config);
  }

  loading(onDismiss?: () => void, message?: string | Signal<string>, title?: string | Signal<string>): Uuid
  {
    const config: LoadingMessageBarConfig = {
      type: 'loading',
      message,
      title: title ?? '#UI_CORE.MESSAGE_BAR.LOADING',
      icon: 'fa-solid fa-spinner fa-spin',
      duration: Number.POSITIVE_INFINITY,
      dismiss: true,
      onDismiss,
    };
    return this.open(config);
  }

  saving(onDismiss?: () => void, message?: string | Signal<string>, title?: string | Signal<string>): Uuid
  {
    const config: SavingMessageBarConfig = {
      type: 'saving',
      message,
      title: title ?? '#UI_CORE.MESSAGE_BAR.LOADING',
      icon: 'fa-solid fa-spinner fa-spin',
      duration: Number.POSITIVE_INFINITY,
      dismiss: true,
      onDismiss,
    };
    return this.open(config);
  }

  progress(onDismiss?: () => void, message?: string, title?: string, options?: MessageBarOptions): Uuid
  {
    const config: ProgressMessageBarConfig = {
      type: 'progress',
      message,
      title: title ?? '#UI_CORE.MESSAGE_BAR.LOADING',
      icon: 'fa-solid fa-spinner fa-spin',
      duration: Number.POSITIVE_INFINITY,
      dismiss: !!onDismiss,
      onDismiss,
    };
    return this.open(config);
  }

}
